import { useState, useEffect } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { api } from "@/lib/api";
import { ArrowLeft, Plus, X } from "lucide-react";
import { toast } from "sonner";
import { LangSwitcher } from "@/components/LangSwitcher";

export default function CampaignForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [businessName, setBusinessName] = useState("");
  const [welcomeMessage, setWelcomeMessage] = useState("");
  const [thankyouMessage, setThankyouMessage] = useState("");
  const [questions, setQuestions] = useState([""]);
  const [countdownSeconds, setCountdownSeconds] = useState(3);
  const [campaignLocale, setCampaignLocale] = useState("pt");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (id) {
      api.get(`/campaigns/${id}`).then((r) => {
        setBusinessName(r.data.business_name);
        setWelcomeMessage(r.data.welcome_message);
        setThankyouMessage(r.data.thankyou_message || "");
        setQuestions(r.data.questions.length ? r.data.questions : [""]);
        const cd = r.data.countdown_seconds;
        setCountdownSeconds([0, 3, 5].includes(cd) ? cd : 3);
        setCampaignLocale(["pt", "en"].includes(r.data.locale) ? r.data.locale : "pt");
      });
    }
  }, [id]);

  const submit = async (e) => {
    e.preventDefault();
    if (!businessName.trim()) return toast.error(t("campaignForm.businessName"));
    setSaving(true);
    try {
      const payload = {
        business_name: businessName,
        welcome_message: welcomeMessage,
        thankyou_message: thankyouMessage,
        questions: questions.filter((q) => q.trim()),
        countdown_seconds: countdownSeconds,
        locale: campaignLocale,
      };
      if (id) {
        await api.put(`/campaigns/${id}`, payload);
        toast.success(t("campaignForm.saved"));
        navigate(`/campaigns/${id}`);
      } else {
        const r = await api.post("/campaigns", payload);
        toast.success(t("campaignForm.created"));
        navigate(`/campaigns/${r.data.id}`);
      }
    } catch {
      toast.error(t("campaignForm.saveError"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      <header className="border-b border-zinc-200 bg-white">
        <div className="max-w-3xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/dashboard" data-testid="back-to-dashboard-link" className="text-sm text-zinc-500 flex items-center gap-1.5 hover:text-zinc-800 transition-colors">
            <ArrowLeft size={15} /> {t("campaignDetail.back")}
          </Link>
          <LangSwitcher />
        </div>
      </header>
      <main className="max-w-3xl mx-auto px-6 py-10">
        <h1 className="font-heading font-extrabold text-3xl tracking-tight mb-8">
          {id ? t("campaignForm.editTitle") : t("campaignForm.newTitle")}
        </h1>
        <form onSubmit={submit} className="bg-white border border-zinc-200 rounded-lg p-8 space-y-8">
          <div>
            <label className="block text-sm font-semibold mb-2">{t("campaignForm.businessName")}</label>
            <input
              data-testid="business-name-input"
              value={businessName}
              onChange={(e) => setBusinessName(e.target.value)}
              placeholder={t("campaignForm.businessNamePh")}
              className="w-full px-4 py-3 bg-[#F4F4F5] border-none rounded-md focus:ring-2 focus:ring-[#FF4F00] focus:outline-none text-sm"
            />
          </div>
          <div>
            <label className="block text-sm font-semibold mb-2">{t("campaignForm.welcome")}</label>
            <textarea
              data-testid="welcome-message-input"
              value={welcomeMessage}
              onChange={(e) => setWelcomeMessage(e.target.value)}
              rows={3}
              placeholder={t("campaignForm.welcomePh")}
              className="w-full px-4 py-3 bg-[#F4F4F5] border-none rounded-md focus:ring-2 focus:ring-[#FF4F00] focus:outline-none text-sm resize-none"
            />
          </div>
          <div>
            <label className="block text-sm font-semibold mb-2">{t("campaignForm.thankyou")}</label>
            <p className="text-xs text-zinc-500 mb-2">{t("campaignForm.thankyouHint")}</p>
            <textarea
              data-testid="thankyou-message-input"
              value={thankyouMessage}
              onChange={(e) => setThankyouMessage(e.target.value)}
              rows={3}
              maxLength={500}
              placeholder={t("campaignForm.thankyouPh")}
              className="w-full px-4 py-3 bg-[#F4F4F5] border-none rounded-md focus:ring-2 focus:ring-[#FF4F00] focus:outline-none text-sm resize-none"
            />
            <p className="text-[11px] text-zinc-400 mt-1 text-right">{thankyouMessage.length}/500</p>
          </div>
          <div>
            <label className="block text-sm font-semibold mb-1">{t("campaignForm.questions")}</label>
            <p className="text-xs text-zinc-500 mb-3">{t("campaignForm.questionsHint")}</p>
            <div className="space-y-2">
              {questions.map((q, i) => (
                <div key={i} className="flex gap-2">
                  <input
                    data-testid={`question-input-${i}`}
                    value={q}
                    onChange={(e) => setQuestions(questions.map((v, j) => (j === i ? e.target.value : v)))}
                    placeholder={`${i + 1}`}
                    className="flex-1 px-4 py-3 bg-[#F4F4F5] border-none rounded-md focus:ring-2 focus:ring-[#FF4F00] focus:outline-none text-sm"
                  />
                  {questions.length > 1 && (
                    <button type="button" data-testid={`remove-question-btn-${i}`} onClick={() => setQuestions(questions.filter((_, j) => j !== i))} className="text-zinc-400 hover:text-red-500 transition-colors px-1">
                      <X size={16} />
                    </button>
                  )}
                </div>
              ))}
            </div>
            {questions.length < 4 && (
              <button type="button" data-testid="add-question-btn" onClick={() => setQuestions([...questions, ""])} className="mt-3 text-sm font-semibold text-[#FF4F00] flex items-center gap-1 hover:underline">
                <Plus size={14} /> {t("campaignForm.addQuestion")}
              </button>
            )}
          </div>
          <div>
            <label className="block text-sm font-semibold mb-1">{t("campaignForm.countdownLabel")}</label>
            <p className="text-xs text-zinc-500 mb-3">{t("campaignForm.countdownHint")}</p>
            <div className="grid grid-cols-3 gap-2">
              {[
                { value: 0, label: t("campaignForm.countdownOff") },
                { value: 3, label: "3s" },
                { value: 5, label: "5s" },
              ].map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  data-testid={`countdown-option-${opt.value}`}
                  onClick={() => setCountdownSeconds(opt.value)}
                  className={`py-3 rounded-lg border-2 font-semibold text-sm transition-colors ${
                    countdownSeconds === opt.value
                      ? "border-[#FF4F00] bg-orange-50 text-[#FF4F00]"
                      : "border-zinc-200 bg-white text-zinc-600 hover:border-zinc-400"
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="block text-sm font-semibold mb-1">{t("campaignForm.localeLabel")}</label>
            <p className="text-xs text-zinc-500 mb-3">{t("campaignForm.localeHint")}</p>
            <div className="grid grid-cols-2 gap-2">
              {[
                { value: "pt", label: "🇧🇷 Português (BR)" },
                { value: "en", label: "🇺🇸 English" },
              ].map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  data-testid={`campaign-locale-${opt.value}`}
                  onClick={() => setCampaignLocale(opt.value)}
                  className={`py-3 rounded-lg border-2 font-semibold text-sm transition-colors ${
                    campaignLocale === opt.value
                      ? "border-[#FF4F00] bg-orange-50 text-[#FF4F00]"
                      : "border-zinc-200 bg-white text-zinc-600 hover:border-zinc-400"
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>
          <button
            data-testid="save-campaign-btn"
            type="submit"
            disabled={saving}
            className="w-full py-3.5 bg-[#FF4F00] text-white rounded-full font-semibold hover:bg-[#E64600] transition-colors disabled:opacity-50"
          >
            {saving ? t("common.saving") : id ? t("campaignForm.saveEdit") : t("campaignForm.saveCreate")}
          </button>
        </form>
      </main>
    </div>
  );
}
